import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { AccountCreationForm } from '../../components/forms/AccountCreationForm';
import { ErrorOverlay } from '../../components/ui/ErrorOverlay';
import {
  queueAccountCreationSharePointSync,
  submitAccountCreation,
} from '../../lib/applicationFormSubmissions';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import type { AccountCreationFormData } from '../../types/applicationForm';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { useFormWorkflowPermissions } from '../../hooks/useFormWorkflowPermissions';
import { formatNotifyRecipientNames } from '../../lib/applicationFormPortalEmail';

export function AccountCreationNewPage() {
  const { basePath } = usePortal();
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const { canSubmitInternal, loading: permissionsLoading } = useFormWorkflowPermissions();
  const [busy, setBusy] = useState(false);
  const [doneNo, setDoneNo] = useState<string | null>(null);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);

  const listPath = basePath ? `${basePath}/account-creation` : '/account-creation';

  const handleSubmit = async (
    companyName: string,
    formData: AccountCreationFormData,
    pendingFiles?: { acra?: File[]; supporting?: File[] },
  ) => {
    setBusy(true);
    try {
      const { submission, notify } = await submitAccountCreation(
        companyName,
        formData,
        'internal',
        resolveInternalSubmitterLabel(profile, authUser, unifiedUsers),
        pendingFiles,
        authUser?.email ?? profile?.email ?? undefined,
      );
      void queueAccountCreationSharePointSync(submission.id);
      setDoneNo(submission.application_no);
      setEmailSentTo(null);
      if (!notify.ok) {
        setEmailNotice(notify.error ?? 'Email notifications could not be sent. Check Settings and Edge Function logs.');
      } else if ((notify.sent?.length ?? 0) === 0) {
        const reasons = (notify.skipped ?? []).map((s) => `${s.kind}: ${s.reason}`).join(' · ');
        setEmailNotice(
          reasons
            ? `Application saved but no emails were sent (${reasons}).`
            : notify.reason ?? 'Application saved but no emails were sent.',
        );
      } else {
        setEmailNotice(null);
        const recipients = [...new Set((notify.sent ?? []).flatMap((item) => item.to))];
        setEmailSentTo(
          formatNotifyRecipientNames(recipients, notify.recipientLabels ?? []),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  if (doneNo) {
    return (
      <div className="page">
        {emailNotice ? (
          <ErrorOverlay message={emailNotice} onClose={() => setEmailNotice(null)} />
        ) : null}
        <div className="card">
          <h1>Submitted</h1>
          <p className="page-lead">
            Application <strong>{doneNo}</strong> has been submitted for review.
          </p>
          {emailSentTo ? (
            <p className="alert alert-success">Email notifications sent to: {emailSentTo}</p>
          ) : null}
          <Link to={listPath} className="btn-primary">
            Back to list
          </Link>
        </div>
      </div>
    );
  }

  if (permissionsLoading) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Checking submission permissions…</p>
      </div>
    );
  }

  if (!canSubmitInternal) {
    return (
      <div className="af-applications-page af-form-page">
        <PageHeader
          title="New Account Application"
          description="Internal submission is not available for your department on this form."
          actions={
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          }
        />
        <p className="alert alert-error">
          Your department is not configured to submit this form internally. Ask IT or Management to update
          Application Form workflow settings.
        </p>
      </div>
    );
  }

  return (
    <div className="af-applications-page af-form-page cff-page--floating">
      <PageHeader
        title="New Account Application"
        description="Internal data entry for Account Creation."
        actions={
          <Link to={listPath} className="btn-secondary btn-sm">
            Back to list
          </Link>
        }
      />
      <div className="card af-form-card">
        <AccountCreationForm
          busy={busy}
          requireDeclaration={false}
          requireSalespersonCode
          submitLabel="Submit for review"
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}
